import React from 'react';
import { motion } from 'framer-motion';
import { Download, FileText, BookOpen, Shield, Leaf, Award, Users, Zap, Utensils, ArrowRight, Mail, Calendar } from 'lucide-react';

const DownloadableResources = () => {
  const resources = [
    {
      title: "Complete ISO 9001 Implementation Guide",
      description: "Step-by-step guide to implementing Quality Management Systems in your organization.",
      type: "Implementation Guide",
      pages: "45 pages",
      icon: <Award className="w-6 h-6" />,
      color: "from-blue-500 to-blue-700",
      downloadUrl: "/resources/ISO-9001-Implementation-Guide.html"
    },
    {
      title: "ISO 14001 Environmental Compliance Checklist",
      description: "Essential checklist for environmental management system implementation.",
      type: "Compliance Checklist",
      pages: "12 pages",
      icon: <Leaf className="w-6 h-6" />,
      color: "from-green-500 to-green-700",
      downloadUrl: "/resources/ISO-14001-Environmental-Checklist.html"
    },
    {
      title: "ISO 27001 Security Assessment Template",
      description: "Comprehensive template for information security risk assessment and management.",
      type: "Excel Template",
      pages: "25 sheets",
      icon: <Shield className="w-6 h-6" />,
      color: "from-purple-500 to-purple-700",
      downloadUrl: "/resources/ISO-27001-Security-Assessment-Template.csv"
    },
    {
      title: "ISO 45001 Health & Safety Whitepaper",
      description: "Industry insights on occupational health and safety management systems.",
      type: "Industry Whitepaper",
      pages: "32 pages",
      icon: <Users className="w-6 h-6" />,
      color: "from-orange-500 to-orange-700",
      downloadUrl: "/resources/ISO-45001-Health-Safety-Whitepaper.html"
    },
    {
      title: "ISO 22000 Food Safety Manual",
      description: "Complete manual for implementing food safety management systems.",
      type: "Implementation Manual",
      pages: "67 pages",
      icon: <Utensils className="w-6 h-6" />,
      color: "from-red-500 to-red-700",
      downloadUrl: "/resources/ISO-22000-Food-Safety-Manual.html"
    },
    {
      title: "ISO 50001 Energy Management ROI Calculator",
      description: "Calculate the return on investment for energy management system implementation.",
      type: "Interactive Calculator",
      pages: "Web-based",
      icon: <Zap className="w-6 h-6" />,
      color: "from-yellow-500 to-yellow-700",
      downloadUrl: "/resources/ISO-50001-Energy-Management-Calculator.html"
    },
    {
      title: "Multi-Standard Integration Guide",
      description: "Best practices for implementing multiple ISO standards efficiently.",
      type: "Strategy Guide",
      pages: "28 pages",
      icon: <BookOpen className="w-6 h-6" />,
      color: "from-indigo-500 to-indigo-700",
      downloadUrl: "/resources/Multi-Standard-Integration-Guide.html"
    },
    {
      title: "ISO Implementation Cost Analysis Report",
      description: "Industry benchmark data and cost analysis for ISO implementation projects.",
      type: "Research Report",
      pages: "54 pages",
      icon: <FileText className="w-6 h-6" />,
      color: "from-teal-500 to-teal-700",
      downloadUrl: "/resources/ISO-Implementation-Cost-Analysis-Report.html"
    }
  ];

  const handleDownload = (resource) => {
    // For HTML files, open in new tab. For CSV files, trigger download
    if (resource.downloadUrl.endsWith('.html')) {
      window.open(resource.downloadUrl, '_blank');
    } else {
      // Create a link element and trigger download for non-HTML files
      const link = document.createElement('a');
      link.href = resource.downloadUrl;
      link.download = resource.downloadUrl.split('/').pop(); // Get filename from URL
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <section id="resources-section" className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Free ISO Resources
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Download comprehensive guides, templates, and tools to accelerate your ISO implementation journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                // Set form mode and scroll to contact section
                window.dispatchEvent(new CustomEvent('setContactMode', { detail: 'form' }));
                window.dispatchEvent(new CustomEvent('showDogPopup', { detail: "I won't read the message but my owner will" }));
                document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg"
            >
              <Mail className="w-4 h-4" />
              Send us a message
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                // Set booking mode and scroll to contact section
                window.dispatchEvent(new CustomEvent('setContactMode', { detail: 'booking' }));
                window.dispatchEvent(new CustomEvent('showDogPopup', { detail: "I won't be in the call, but my master will" }));
                document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-white border-2 border-blue-600 text-blue-600 hover:bg-gray-50 px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300"
            >
              <Calendar className="w-4 h-4" />
              Book a call
            </motion.button>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {resources.map((resource, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.02 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group"
            >
              <div className={`h-20 bg-gradient-to-r ${resource.color} flex items-center justify-center`}>
                <div className="text-white">
                  {resource.icon}
                </div>
              </div>
              
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {resource.type}
                  </span>
                  <span className="text-xs text-gray-500">
                    {resource.pages}
                  </span>
                </div>
                
                <h3 className="text-lg font-bold text-gray-800 mb-3 leading-tight">
                  {resource.title}
                </h3>
                
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  {resource.description}
                </p>
                
                <motion.button
                  onClick={() => handleDownload(resource)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full bg-gradient-to-r ${resource.color} text-white py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 group-hover:shadow-lg`}
                >
                  <Download className="w-4 h-4" />
                  Download Free
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call-to-Action */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 text-center bg-white p-8 rounded-3xl shadow-xl"
        >
          <h3 className="text-3xl font-bold mb-4 text-gray-800">
            Need Personalized Guidance?
          </h3>
          <p className="text-lg text-gray-600 mb-6">
            Get a customized implementation plan and one-on-one consultation with our ISO experts.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-2xl text-lg font-semibold transition-all duration-300 shadow-xl"
          >
            Schedule Free Consultation
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default DownloadableResources;